import { useEffect, useRef } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useScript } from 'usehooks-ts'

import { OrderAmount } from '@entities/Order'

import useAddressPickerModal from '@features/address-picker/hooks/useAddressPickerModal'
import {
  useAuth,
  useLoginModal,
  useProfileCustomerModal,
} from '@features/auth'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'
import useModalStore from '@features/modal/model'
import { DeliveryForm, PickupForm, UserInfoForm } from '@features/order-form'
import { OrderFormValues } from '@features/order-form/model/shema'
import { useOrderForm } from '@features/order-form/model/useOrderForm.ts'
import DetailOrderForm from '@features/order-form/ui/DetailOrderForm/DetailOrderForm'
import { useAddPayment } from '@features/payment/hooks/useAddPayment'
import { useAddressStore } from '@features/shipping-methods'

import { useHeaderStore } from '@widgets/header/model/headerStore'
import { useSlots, useSlotStore, useSyncSlot } from '@widgets/slots'


import { ReplacementStrategyVariants, URL_CUSTOMER_ORDERS, URL_CUSTOMER_PROFILE } from '@shared/contants'
import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import { Form } from '@shared/ui/form'
import Wrapper from '@shared/ui/wrapper'
import { formatPhoneNumber } from '@shared/uttils/formatPhoneNumber'
import { splitFullName } from '@shared/uttils/splitFullName'

import { usePendingOrder } from '../hooks/usePendingOrder'
import SuccessModal from './modal/SuccessModal'

import styles from './styles.module.scss'


const PAYMENT_SCRIPT: string = import.meta.env.VITE_HALYK_SCRIPT_URL

export default function OrderDelivery () {
  const { setVariant } = useHeaderStore()
  const { user, fetchPingUser, requireRegistration } = useAuth()
  const { openProfileCustomerModal } = useProfileCustomerModal()
  const { openLoginModal } = useLoginModal()
  const { selectedAddress, isPickup, shopId, zoneId } = useAddressStore()
  const navigate = useNavigate()
  const scriptStatus = useScript(PAYMENT_SCRIPT)
  const { openAddressPickerModal } = useAddressPickerModal()
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { beginCheckout, addShippingInfo, addPaymentInfo, purchase } = useAnalytics()
  const hasTrackedBeginCheckout = useRef(false)
  const trackedShippingSlotRef = useRef<string | null>(null)

  const {
    pendingOrder,
    pendingOrderId,
    loading: orderLoading,
    updateInProgress,
    updateAddress,
    updateRecipientInfo,
    updateSlot,
    updateCourierMessage,
    updateAssemblingDetails,
    placeOrder,
    placeOrderLoading,
    resetPaymentToken
  } = usePendingOrder({
    shopId,
    isAvailable: !!(user && !requireRegistration && shopId)
  })

  // Синхронизация слота из pendingOrder и автовыбор первого доступного
  const { loading: slotLoading } = useSyncSlot({
    pendingOrderSlotId: pendingOrder?.slot_id,
    isAvailable: !!(user && !requireRegistration && shopId)
  })

  useEffect(() => {
    async function checkModals () {
      const data = await fetchPingUser()
      const auth = data.data?.pingAuth
      const isRequireRegistration = auth?.requireRegistration

      if (!auth) {
        openLoginModal(() => {
          navigate('/cart')
        })
        return
      }

      if (isRequireRegistration) {
        openProfileCustomerModal(() => {
          navigate('/cart')
        })
        return
      }
      if (!selectedAddress || selectedAddress.isVirtual) {
        openAddressPickerModal()
        return
      }
    }

    checkModals()
  }, [])

  const methods = useOrderForm() as UseFormReturn<OrderFormValues>
  const { t: o } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const { clearCart, cartInfoData } = useCartStore()
  const { openModal } = useModalStore()
  const { openSlotsModal } = useSlots()
  const watched = useWatch<OrderFormValues>({
    control: methods.control,
  })
  const { selectedSlot } = useSlotStore()
  const { addPayment } = useAddPayment()

  // Track begin_checkout when user enters checkout page
  useEffect(() => {
    if (cartInfoData?.products?.length && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true
      const validProducts = cartInfoData.products.filter((p): p is NonNullable<typeof p> => p !== null && p.info !== null)
      if (validProducts.length > 0) {
        beginCheckout(validProducts)
      }
    }
  }, [ cartInfoData?.products, beginCheckout ])

  // Track add_shipping_info when slot is selected/changed
  useEffect(() => {
    const slotId = selectedSlot?.id ? String(selectedSlot.id) : null
    if (!slotId || trackedShippingSlotRef.current === slotId || !cartInfoData?.products?.length) {
      return
    }

    const validProducts = cartInfoData.products.filter((p): p is NonNullable<typeof p> => p !== null && p.info !== null)
    if (validProducts.length === 0) {
      return
    }

    trackedShippingSlotRef.current = slotId
    addShippingInfo(validProducts, isPickup() ? 'pickup' : 'slot')
  }, [ selectedSlot?.id, cartInfoData?.products, addShippingInfo, isPickup ])

  useEffect(() => {
    setVariant('minimal')
    return () => setVariant('default')
  }, [])

  useEffect(() => {
    if (!user) return

    // When selectedAddress changes, address fields should reflect the new address.
    // pendingOrder fields are only used as fallback for non-address fields.
    methods.reset({
      name: user.first_name || '',
      phone: formatPhoneNumber(user.phone || ''),
      address: selectedAddress?.address ?? pendingOrder?.address ?? '',
      pickup: isPickup(),
      replacementOption: pendingOrder?.assembly_method || ReplacementStrategyVariants.CALL_ON_MISSING,
      commentBuilder: pendingOrder?.comment || '',
      isPrivateHouse: selectedAddress?.isPrivateHouse ?? false,
      apartment: selectedAddress?.address_appartments ?? '',
      floor: selectedAddress?.address_floor ?? '',
      entrance: selectedAddress?.address_porch ?? '',
      intercom: selectedAddress?.address_intercom ?? '',
      comment: selectedAddress?.comment ?? '',
      nameOther: selectedAddress?.recipient_name ?? '',
      phoneOther: selectedAddress?.recipient_phone ?? '',
      isOtherPerson: selectedAddress?.other_recipient ?? false,
    })
  }, [ user, pendingOrder, selectedAddress, methods, isPickup ])

  const onSubmit = async (selected: string) => {
    const successRedirectUrl = `${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ORDERS}`

    // Track add_payment_info before placing order
    if (cartInfoData?.products) {
      const validProducts = cartInfoData.products.filter((p): p is NonNullable<typeof p> => p !== null && p.info !== null)
      if (validProducts.length > 0) {
        addPaymentInfo(validProducts, selected === 'new-card' ? 'new_card' : 'saved_card')
      }
    }

    if (selected !== 'new-card') {
      try {
        const { orderId, isOrderPlacedSuccessfully, errorShape } = await placeOrder()
        if (isOrderPlacedSuccessfully && orderId) {
          // Track purchase event
          if (cartInfoData?.products && cartInfoData?.totals?.total) {
            const validProducts = cartInfoData.products.filter((p): p is NonNullable<typeof p> => p !== null && p.info !== null)
            if (validProducts.length > 0) {
              purchase(orderId, validProducts, {
                shipping: cartInfoData.delivery?.total ?? undefined,
              })
            }
          }

          openModal(
            'successPaymentOrder',
            <SuccessModal selectSlot={selectedSlot} id={orderId} />,
            {
              onClose () {
                navigate(successRedirectUrl)
              }
            }
          )
          clearCart()
        }

        if (!errorShape.isOk()) {
          const i18nKey = errorShape.getAppErrorCode()

          openErrorInfoModal({
            title: o(`place_order_error.${i18nKey}`),
            description: o(`place_order_error.${i18nKey}_description`),
            extendedErrorMessage: JSON.stringify({
              message: errorShape.getMessage(),
              code: errorShape.getAppErrorCode(),
              orderId,
              placed: isOrderPlacedSuccessfully,
            }, null, 2),
          })
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catch (e: any) {
        console.error('Ошибка GraphQL или сети:', e)
        openErrorInfoModal({
          description: te('create_order'),
          extendedErrorMessage: e.message
        })
      }
    }
    else {
      addPayment(pendingOrderId as string, successRedirectUrl)
    }
  }

  const {
    comment,
    noIntercomCall,
    replacementOption,
    nameOther,
    phoneOther,
    leaveAtDoor,
    commentBuilder,
    apartment,
    entrance,
    floor,
    intercom
  } = watched

  useEffect(() => {
    const { firstName, lastName } = splitFullName(nameOther || '')

    updateRecipientInfo({
      firstName,
      lastName,
      phone: phoneOther,
    })
  }, [ nameOther, phoneOther, updateRecipientInfo ])

  useEffect(() => {
    updateAddress({
      address: selectedAddress?.address || '',
      address_appartments: apartment || selectedAddress?.address_appartments,
      address_intercom: intercom || selectedAddress?.address_intercom,
      address_floor: floor || selectedAddress?.address_floor,
      address_porch: entrance || selectedAddress?.address_porch,
      customerGeoPoint: selectedAddress?.customerGeoPoint,
      comment: commentBuilder || selectedAddress?.comment || '',
      door_delivery: leaveAtDoor,
      no_door_call: noIntercomCall,
    }, isPickup(), zoneId || undefined)
  }, [ updateAddress, selectedAddress, isPickup, leaveAtDoor, noIntercomCall, commentBuilder, apartment, intercom, floor, entrance, zoneId ])

  useEffect(() => {
    if (selectedSlot?.id) {
      updateSlot(+selectedSlot.id)
    }
  }, [ selectedSlot?.id, updateSlot ])

  useEffect(() => {
    if (comment) {
      updateCourierMessage(comment)
    }
  }, [ comment, updateCourierMessage ])

  useEffect(() => {
    updateAssemblingDetails({
      comment: commentBuilder,
      assembly_method: replacementOption,
    })
  }, [ commentBuilder, replacementOption, updateAssemblingDetails ])

  const handlePaymentMethodReset = () => {
    resetPaymentToken()
  }

  const isAddressValid = !!(selectedAddress && !selectedAddress.isVirtual)
  const isZoneValid = isPickup() || !!zoneId

  return (
    <Container>
      <div className={styles.root}>
        <div className={styles.root__left}>
          <div className={styles.wrapper}>
            <Form {...methods}>
              <form className={styles.root__form}>
                {/* Delivery */}
                {isPickup() ? <PickupForm /> : <DeliveryForm />}

                {/* Customer form */}
                <UserInfoForm isPickup={isPickup()} />

                {/* Slot form */}
                {selectedSlot && selectedAddress && !selectedAddress.isVirtual && (
                  <Wrapper className={styles.banner}>
                    <h3>{o('data_time')}</h3>
                    <div className={styles.time}>
                      <span>
                        {selectedSlot.slotDate} {tc('from')}{' '}
                        {selectedSlot.slotTime}
                      </span>
                      <Button
                        className={styles.time__button}
                        theme="quaternary"
                        onClick={() => {
                          openSlotsModal()
                        }}
                      >
                        {tc('change')}
                      </Button>
                    </div>
                  </Wrapper>
                )}

                {/* Order details */}
                <DetailOrderForm />
              </form>
            </Form>

            {/* {isPickup() && (
              <Wrapper className={styles.banner}>
                <h3>{o('banner.title')}</h3>
                <p>{o('banner.description')}</p>
              </Wrapper>
            )} */}
          </div>
        </div>

        <OrderAmount
          loading={
            placeOrderLoading || updateInProgress || orderLoading || slotLoading || scriptStatus === 'loading'
          }
          onSubmit={onSubmit}
          canSubmit={isAddressValid && isZoneValid}
          submitButtonText={!isAddressValid ? o('select_address_button') : !isZoneValid ? o('select_zone_button') : undefined}
          orderId={pendingOrderId}
          onPaymentMethodReset={handlePaymentMethodReset}
        />
      </div>
    </Container>
  )
}
